<template>
    <dialog
        ref="dialog"
        v-bind="$attrs"
        :class="variantStyles"
        :inert="!value"
        @close="onDialogClose"
    >
        <slot name="dialog-content">
            <div class="relative max-h-dvh">
                <slot />
            </div>
        </slot>
    </dialog>
</template>

<script>
export default {
    name: "DialogComponent",
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: "default",
            validator: (val) => ["default", "none"].includes(val),
        },
        modal: {
            type: Boolean,
            default: true,
        },
        drawer: {
            type: String,
            default: undefined,
            validator: (val) => !val || ["ltr", "rtl", "ttb", "btt"].includes(val),
        },
        enableLightDismiss: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            boundLightDismiss: null,
        };
    },
    computed: {
        variantStyles() {
            if (this.variant === "none") {
                return "";
            } else if (this.drawer !== undefined) {
                return `el-drawer el-drawer-${this.drawer}`;
            } else if (this.modal) {
                return "el-dialog el-modal";
            } else {
                return "el-dialog";
            }
        },
    },
    created() {
        this.boundLightDismiss = this.lightDismiss.bind(this);
    },
    watch: {
        value: {
            handler(newValue) {
                if (newValue) {
                    this.$nextTick(() => {
                        const dialog = this.$refs.dialog;
                        if (dialog && !dialog.open) {
                            this.showDialog();
                        }
                    });

                    setTimeout(() => {
                        const dialog = this.$refs.dialog;
                        const autoFocusEl = dialog?.querySelector("[autofocus]");
                        autoFocusEl?.focus();
                    }, 100);

                    if (this.enableLightDismiss) {
                        setTimeout(() => {
                            document.addEventListener("click", this.boundLightDismiss);
                        }, 100);
                    }
                } else {
                    this.closeDialog();

                    if (this.enableLightDismiss) {
                        document.removeEventListener("click", this.boundLightDismiss);
                    }
                }
            },
            immediate: true,
        },
    },
    methods: {
        showDialog() {
            const dialog = this.$refs.dialog;
            if (!dialog) return;

            if (this.modal) {
                dialog.showModal();
            } else {
                dialog.show();
            }
        },
        closeDialog() {
            const dialog = this.$refs.dialog;
            if (dialog) {
                dialog.close();
            }
        },
        toggle() {
            this.$emit("input", !this.value);
        },
        onDialogClose() {
            this.$emit("input", false);
        },
        lightDismiss(event) {
            const target = event.target;
            const isDialog = target instanceof HTMLDialogElement;
            const isHTMLElementNotDialog = target instanceof Element && !target.closest("dialog");
            const isModalOrDrawer = this.modal || this.drawer !== undefined;

            if ((isModalOrDrawer && isDialog) || (!this.modal && isHTMLElementNotDialog)) {
                this.closeDialog();
                this.$emit("input", false);
            }
        },
    },
};
</script>

<style scoped>
.relative {
    position: relative;
}

.max-h-dvh {
    max-height: 100dvh;
}

.el-drawer-fullscreen {
    min-height: 100dvh;
    min-width: 100dvw;
}

/* Dialog and Modal Styles */
.el-dialog {
    --animation-timing-entrance: 300ms;
    --animation-timing-exit: 300ms;

    overflow: hidden;
    display: block;
    padding: 0;
    background-color: white;
    transition: opacity 300ms ease;
    transition-behavior: allow-discrete;
}

.el-modal {
    position: fixed;
    inset: 0;
    margin: auto;
    border-radius: 8px;

    transition-property: opacity display overlay;
    transition-duration: 300ms;
    transition-behavior: allow-discrete;
}

.el-dialog:not([open]) {
    pointer-events: none;
    opacity: 0;
}

.el-modal::backdrop {
    position: fixed;
    inset: 0;
    opacity: 0;
    transition-property: opacity display overlay;
    transition-duration: 300ms;
    transition-behavior: allow-discrete;
}

.el-modal[open]::backdrop {
    opacity: 1;
}

@starting-style {
    .el-modal[open]::backdrop {
        opacity: 0;
    }
}

html:has(.el-modal[open]) {
    overflow: hidden;
}

@media (prefers-reduced-motion: no-preference) {
    .el-dialog {
        animation: slide-out-down forwards var(--animation-timing-exit);
        animation-timing-function: ease-in-out;
    }

    .el-dialog[open] {
        animation: slide-in-up forwards var(--animation-timing-entrance) ease;
    }
}

@keyframes slide-out-down {
    100% {
        transform: translateY(20%);
    }
}

@keyframes slide-in-up {
    0% {
        transform: translateY(20%);
    }
}

/* Drawer Dialog Styles */
.el-drawer {
    --animation-timing: 0ms;

    background-color: white;
    position: fixed;
    inset: unset;
    border-radius: 0;

    transition-property: display overlay;
    transition-duration: var(--animation-timing);
    transition-behavior: allow-discrete;
}

.el-drawer:not([open]) {
    pointer-events: none;
}

.el-drawer::backdrop {
    position: fixed;
    inset: 0;
    opacity: 0;
    transition-property: opacity display overlay transform;
    transition-duration: var(--animation-timing);
    transition-behavior: allow-discrete;
}

.el-drawer[open]::backdrop {
    opacity: 1;
    background-color: rgb(0, 0, 0, 0.7);
}

.el-drawer:not([open]) {
    opacity: 1;
}

html:has(.el-drawer[open]) {
    overflow: hidden;
}

.el-drawer-ltr {
    min-height: 100dvh;
    left: 0;
    top: 0;
    bottom: 0;
    transform: translateX(-100%);

    &[open] {
        transform: translateX(0%);
    }

    & > :where(div) {
        min-height: 100dvh;
    }
}

.el-drawer-rtl {
    min-height: 100dvh;
    left: 100%;
    top: 0;
    bottom: 0;

    &[open] {
        transform: translateX(-100%);
    }

    & > :where(div) {
        min-height: 100dvh;
        width: max-content;
    }
}

.el-drawer-ttb {
    min-width: 100dvw;
    left: 0;
    right: 0;
    top: 0;
    transform: translateY(-100%);

    &[open] {
        transform: translateY(0%);
    }

    & > :where(div) {
        min-width: 100dvw;
    }
}

.el-drawer-btt {
    min-width: 100dvw;
    left: 0;
    right: 0;
    top: 100%;

    &[open] {
        transform: translateY(-100%);
    }

    & > :where(div) {
        min-width: 100dvw;
    }
}

@starting-style {
    .el-drawer[open]::backdrop {
        opacity: 0;
    }

    .el-drawer-ltr[open] {
        transform: translateX(-100%);
    }

    .el-drawer-rtl[open] {
        transform: translateX(0%);
    }

    .el-drawer-ttb[open] {
        transform: translateY(-100%);
    }

    .el-drawer-btt[open] {
        transform: translateY(0%);
    }
}

@media (prefers-reduced-motion: no-preference) {
    .el-drawer {
        --animation-timing: 300ms;
    }
}

@media (width <= 480px) {
    .el-drawer {
        min-height: 100dvh;
        min-width: 100dvw;

        & > :where(div) {
            min-height: 100dvh;
            min-width: 100dvw;
        }
    }
}
</style>
